import { Fragment, createElement, useState, type ReactNode } from 'react';

type Run = { text: string; bold?: boolean; italic?: boolean; underline?: boolean; strike?: boolean; script?: 'sub' | 'super'; link?: string };
type Figure = { kind: string; title: string; caption?: string; alt: string; image?: string; rows?: string[][]; headerRow?: boolean; sideText?: string };
export type ReadingBlock = { type: string; runs?: Run[]; level?: number; ordered?: boolean; indent?: number; align?: 'center' | 'right' | 'justify'; figure?: Figure };
export type Preview =
  | { kind: 'document'; blocks: ReadingBlock[] }
  | { kind: 'text'; text: string }
  | { kind: 'spreadsheet'; sheets: { name: string; rows: { index: number; cells: { column: number; text: string }[] }[] }[] }
  | { kind: 'slides'; slides: { parts: { type: string; text?: string; rows?: string[][] }[]; notes: string }[] };

function Runs({ runs = [] }: { runs?: Run[] }) {
  return <>{runs.map((run, index) => {
    let content: ReactNode = run.text;
    if (run.bold) content = <strong>{content}</strong>;
    if (run.italic) content = <em>{content}</em>;
    if (run.underline) content = <u>{content}</u>;
    if (run.strike) content = <s>{content}</s>;
    if (run.script) content = run.script === 'sub' ? <sub>{content}</sub> : <sup>{content}</sup>;
    if (run.link && /^https?:\/\//i.test(run.link)) content = <a href={run.link} target="_blank" rel="noreferrer">{content}</a>;
    return <Fragment key={index}>{content}</Fragment>;
  })}</>;
}

function Table({ rows, label, header = true }: { rows: string[][]; label: string; header?: boolean }) {
  return <div className="reader-table-scroll" role="region" aria-label={label} tabIndex={0}><table>
    {header && <thead><tr>{rows[0]?.map((cell, i) => <th scope="col" key={i}>{cell}</th>)}</tr></thead>}
    <tbody>{rows.slice(header ? 1 : 0).map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
  </table></div>;
}

function SavedFigure({ value }: { value: Figure }) {
  return <figure className="reader-figure">
    <figcaption><strong>{value.title}</strong>{value.caption && <span>{value.caption}</span>}</figcaption>
    {value.kind === 'table' && value.rows ? <Table rows={value.rows} label={value.title} header={value.headerRow} />
      : value.image && /^data:image\/(png|jpeg);base64,/.test(value.image) ? <img src={value.image} alt={value.alt} loading="lazy" />
      : <p className="reader-placeholder">{value.alt}</p>}
    {value.sideText && <p className="assignment-reading-text">{value.sideText}</p>}
  </figure>;
}

function Document({ blocks }: { blocks: ReadingBlock[] }) {
  const elements: ReactNode[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i], key = i;
    const style = block.align ? { textAlign: block.align } : undefined;
    if (block.type === 'list') {
      const items = [];
      do {
        const item = blocks[i];
        items.push(<li key={i} style={{ paddingInlineStart: `${(item.indent ?? 0) * 1.25}em` }}><Runs runs={item.runs}/></li>);
        i++;
      } while (blocks[i]?.type === 'list' && blocks[i]?.ordered === block.ordered);
      i--;
      elements.push(createElement(block.ordered ? 'ol' : 'ul', { key }, items));
    } else if (block.type === 'figure' && block.figure) elements.push(<SavedFigure key={key} value={block.figure}/>);
    else if (block.type === 'heading') elements.push(createElement(`h${Math.max(2, Math.min(6, (block.level ?? 1) + 1))}`, { key, style, id: `reader-heading-${i}`, tabIndex: -1 }, <Runs runs={block.runs}/>));
    else if (block.type === 'quote') elements.push(<blockquote key={key} style={style}><Runs runs={block.runs}/></blockquote>);
    else if (block.type === 'code') elements.push(<pre key={key}><code><Runs runs={block.runs}/></code></pre>);
    else elements.push(<p key={key} style={style}><Runs runs={block.runs}/>{!block.runs?.length && <br/>}</p>);
  }
  return <div className="reader-prose">{elements}</div>;
}

function columnName(index: number): string {
  let name = '';
  for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26)) name = String.fromCharCode(65 + (value - 1) % 26) + name;
  return name;
}

function Spreadsheet({ preview }: { preview: Extract<Preview, { kind: 'spreadsheet' }> }) {
  const [selected, setSelected] = useState(0), [page, setPage] = useState(0), [columnPage, setColumnPage] = useState(0);
  const sheet = preview.sheets[selected];
  const allColumns = [...new Set(sheet?.rows.flatMap(row => row.cells.map(cell => cell.column)) ?? [])].sort((a,b) => a-b);
  const columns = allColumns.slice(columnPage * 20, (columnPage + 1) * 20);
  const rows = sheet?.rows.slice(page * 100, (page + 1) * 100) ?? [];
  return <div className="reader-spreadsheet">
    <nav className="reader-tabs" aria-label="Workbook sheets">{preview.sheets.map((item, index) => <button key={index} aria-pressed={selected === index} onClick={() => { setSelected(index); setPage(0); setColumnPage(0); }}>{item.name}</button>)}</nav>
    {!sheet?.rows.length ? <p className="reader-placeholder">No saved cells in this sheet.</p> : <>
      <p className="reader-caption">Saved cell values · Empty rows and columns are omitted. Original cell addresses are shown.</p>
      <div className="reader-table-scroll reader-cells" role="region" aria-label={`${sheet.name} cells`} tabIndex={0}><table>
        <thead><tr><th scope="col">Row</th>{columns.map(column => <th scope="col" key={column}>{columnName(column)}</th>)}</tr></thead>
        <tbody>{rows.map(row => <tr key={row.index}><th scope="row">{row.index + 1}</th>{columns.map(column => <td key={column}>{row.cells.find(cell => cell.column === column)?.text ?? ''}</td>)}</tr>)}</tbody>
      </table></div>
      {sheet.rows.length > 100 && <nav className="reader-pagination" aria-label="Saved rows"><button disabled={page === 0} onClick={() => setPage(page - 1)}>Previous rows</button><span>Page {page + 1} of {Math.ceil(sheet.rows.length / 100)}</span><button disabled={(page + 1) * 100 >= sheet.rows.length} onClick={() => setPage(page + 1)}>Next rows</button></nav>}
      {allColumns.length > 20 && <nav className="reader-pagination" aria-label="Saved columns"><button disabled={columnPage === 0} onClick={() => setColumnPage(columnPage - 1)}>Previous columns</button><span>{columnName(columns[0])}–{columnName(columns.at(-1)!)}</span><button disabled={(columnPage + 1) * 20 >= allColumns.length} onClick={() => setColumnPage(columnPage + 1)}>Next columns</button></nav>}
    </>}
  </div>;
}

function Slides({ preview }: { preview: Extract<Preview, { kind: 'slides' }> }) {
  const [selected, setSelected] = useState(0);
  const slide = preview.slides[selected];
  return <div className="reader-slides">
    <nav className="reader-tabs" aria-label="Slides">{preview.slides.map((_, index) => <button key={index} aria-pressed={selected === index} onClick={() => setSelected(index)}>Slide {index + 1}</button>)}</nav>
    {slide ? <><div className="reader-slide"><span className="reader-overline">SLIDE {selected + 1} / {preview.slides.length}</span>
      {slide.parts.map((part, index) => part.type === 'table' && part.rows ? <Table key={index} rows={part.rows} label={`Slide ${selected + 1} table`} /> : <p key={index} className={part.type === 'unsupported' ? 'reader-placeholder' : 'assignment-reading-text'}>{part.text}</p>)}
      {!slide.parts.length && <p>No saved content on this slide.</p>}
    </div>{slide.notes && <details className="reader-slide-notes"><summary>Speaker notes</summary><p className="assignment-reading-text">{slide.notes}</p></details>}
    <p className="reader-caption">Slide reading view. Open the PC app for the original layout and visual elements.</p></> : <p>No saved slides yet.</p>}
  </div>;
}

export function ReadingPreview({ preview }: { preview: Preview }) {
  if (preview.kind === 'document') return preview.blocks.some(block => block.type === 'figure' || block.runs?.some(run => run.text.trim())) ? <Document blocks={preview.blocks}/> : <p className="reader-placeholder">No saved content yet. Start working on this file in the PC app.</p>;
  if (preview.kind === 'spreadsheet') return <Spreadsheet preview={preview}/>;
  if (preview.kind === 'slides') return <Slides preview={preview}/>;
  return <div className="assignment-reading-text reader-prose">{preview.text ? preview.text.split('\uFFFC').map((part, index) => <Fragment key={index}>{index > 0 && <span className="reader-placeholder">[Embedded content — view in the PC app]</span>}{part}</Fragment>) : <p className="reader-placeholder">No saved content yet. Start working on this file in the PC app.</p>}</div>;
}
